import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { path: '/', label: 'Home' },
    { 
      label: 'Services', 
      submenu: [
        { path: '/services/crm-implementation', label: 'CRM Implementation' },
        { path: '/services/erp-integration', label: 'ERP Integration' },
        { path: '/services/smart-crm', label: 'Smart CRM' },
        { path: '/services/lead-generation', label: 'Lead Generation' },
        { path: '/services/ai-enablement', label: 'AI Enablement' }
      ]
    },
    { path: '/blog', label: 'Blog' },
    { path: '/about', label: 'About Us' },
    { path: '/contact', label: 'Contact' }
  ];

  return (
    <header style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      backgroundColor: 'rgba(13, 115, 119, 0.70)',
      backdropFilter: 'blur(10px)',
      zIndex: 1000,
      borderBottom: '1px solid rgba(201, 162, 39, 0.3)'
    }}>
      <div className="container" style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '20px 40px',
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
        {/* Logo */}
        <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
          <img 
            src="https://customer-assets.emergentagent.com/job_digital-transform-45/artifacts/zhvprbqi_logomark_web_large_1024x1024.png"
            alt="Resolv Digital"
            style={{ height: '48px', width: 'auto' }}
          />
          <span style={{
            marginLeft: '12px',
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 700,
            fontSize: '1.25rem',
            color: '#C9A227'
          }}>
            RESOLV
          </span>
        </Link>

        {/* Desktop Navigation */}
        <nav style={{
          display: 'flex',
          gap: '32px',
          alignItems: 'center'
        }} className="desktop-nav">
          {navLinks.map((link, index) => (
            link.submenu ? (
              <div 
                key={index} 
                className="nav-dropdown" 
                style={{ position: 'relative' }}
                onMouseEnter={() => setServicesOpen(true)}
                onMouseLeave={() => setServicesOpen(false)}
              >
                <button style={{
                  background: 'none',
                  border: 'none',
                  color: '#ffffff',
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '1rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'color 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 0'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#C9A227'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#ffffff'}
                >
                  {link.label}
                  <ChevronDown size={16} style={{
                    transform: servicesOpen ? 'rotate(180deg)' : 'rotate(0)',
                    transition: 'transform 0.3s ease'
                  }} />
                </button>
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  backgroundColor: '#0a5d60',
                  border: '1px solid #C9A227',
                  borderRadius: '8px',
                  padding: '12px 0',
                  minWidth: '240px',
                  opacity: servicesOpen ? 1 : 0,
                  visibility: servicesOpen ? 'visible' : 'hidden',
                  transform: servicesOpen ? 'translateY(0)' : 'translateY(-10px)',
                  transition: 'all 0.3s ease',
                  marginTop: '8px',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)'
                }}>
                  {link.submenu.map((sublink, subIndex) => (
                    <Link
                      key={subIndex}
                      to={sublink.path}
                      style={{
                        display: 'block',
                        padding: '12px 24px',
                        color: isActive(sublink.path) ? '#C9A227' : '#ffffff',
                        textDecoration: 'none',
                        fontFamily: 'Lato, sans-serif',
                        fontSize: '0.95rem',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = 'rgba(201, 162, 39, 0.1)';
                        e.target.style.color = '#C9A227';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        if (!isActive(sublink.path)) e.target.style.color = '#ffffff';
                      }}
                    >
                      {sublink.label}
                    </Link>
                  ))}
                </div>
              </div>
            ) : (
              <Link
                key={index}
                to={link.path}
                style={{
                  color: isActive(link.path) ? '#C9A227' : '#ffffff',
                  textDecoration: 'none',
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '1rem',
                  fontWeight: 500,
                  transition: 'color 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.color = '#C9A227'}
                onMouseLeave={(e) => !isActive(link.path) && (e.target.style.color = '#ffffff')}
              >
                {link.label}
              </Link>
            )
          ))}
          <Link to="/contact" className="btn-primary" style={{ marginLeft: '16px' }}>
            Get Started
          </Link>
        </nav>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          style={{
            display: 'none',
            background: 'none',
            border: 'none',
            color: '#C9A227',
            cursor: 'pointer',
            padding: '8px'
          }}
          className="mobile-menu-btn"
        >
          {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div style={{
          backgroundColor: '#0D7377',
          borderTop: '1px solid rgba(201, 162, 39, 0.2)',
          padding: '20px'
        }} className="mobile-menu">
          {navLinks.map((link, index) => (
            link.submenu ? (
              <div key={index}>
                <div style={{
                  padding: '12px 0',
                  color: '#C9A227',
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  {link.label}
                </div>
                {link.submenu.map((sublink, subIndex) => (
                  <Link
                    key={subIndex}
                    to={sublink.path}
                    onClick={() => setMobileMenuOpen(false)}
                    style={{
                      display: 'block',
                      padding: '12px 20px',
                      color: isActive(sublink.path) ? '#C9A227' : '#ffffff',
                      textDecoration: 'none',
                      fontFamily: 'Lato, sans-serif',
                      fontSize: '1rem'
                    }}
                  >
                    {sublink.label}
                  </Link>
                ))}
              </div>
            ) : (
              <Link
                key={index}
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                style={{
                  display: 'block',
                  padding: '16px 0',
                  color: isActive(link.path) ? '#C9A227' : '#ffffff',
                  textDecoration: 'none',
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '1.1rem',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                }}
              >
                {link.label}
              </Link>
            )
          ))}
          <Link
            to="/contact"
            onClick={() => setMobileMenuOpen(false)}
            className="btn-primary"
            style={{ marginTop: '20px', width: '100%' }}
          >
            Get Started
          </Link>
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .desktop-nav {
            display: none !important;
          }
          .mobile-menu-btn {
            display: block !important;
          }
        }
        
        @media (min-width: 769px) {
          .mobile-menu {
            display: none !important;
          }
        }
      `}</style>
    </header>
  );
};

export default Header;